import * as React from 'react';
import { useEffect, useState } from 'react';
import {
    ControlledMenu,
    FocusableItem,
    MenuDivider,
    MenuHeader,
    useMenuState,
} from '@szhsin/react-menu';
import '@szhsin/react-menu/dist/core.css';
import { ColorIndex, colorsEqual, PRESET_COLORS } from '@/utils/ColorHelper';
import { getRecentCustomColors } from '@/utils/ColorPreferences';
import '../../../stylesheets/5_components/limiter/what/ColorMenu.less';

export interface ColorMenuAction {
    title: string;
    disabledTitle?: string;
    icon: React.ReactNode;
    onClick: (color: ColorIndex) => void;
    className?: string;
    disabled?: boolean | ((color: ColorIndex) => boolean);
}

export interface ColorMenuActionRow {
    title: string;
    icon: React.ReactNode;
    actions: ColorMenuAction[];
}

interface Props {
    title: string;
    currentColor: ColorIndex | null;
    anchorPoint: { x: number; y: number };
    isOpen: boolean;
    onClose: () => void;
    actionRows: ColorMenuActionRow[];
}

const fallbackColor = new ColorIndex(0, 0);

const getColorInputValue = (color: ColorIndex | null) =>
    color?.customColor ?? PRESET_COLORS[color?.mainColorIndex ?? 0];

const isActionDisabled = (action: ColorMenuAction, color: ColorIndex): boolean =>
    typeof action.disabled === 'function'
        ? action.disabled(color)
        : Boolean(action.disabled);

const getActionTitle = (action: ColorMenuAction, color: ColorIndex): string =>
    isActionDisabled(action, color)
        ? (action.disabledTitle ?? action.title)
        : action.title;

// Right-click color menu shared by the vegobjekttype-level (HvaInfo) and category-level
// (CategoryResult) color pickers. Picking a preset/recent/custom color only stages it as the
// pending selection - nothing is applied until the user chooses an action from a scoped row.
export const ColorMenu: React.FC<Props> = ({
    title,
    currentColor,
    anchorPoint,
    isOpen,
    onClose,
    actionRows,
}) => {
    const [menuProps, toggle] = useMenuState();
    const [selectedColor, setSelectedColor] = useState<ColorIndex>(
        currentColor ?? fallbackColor
    );
    const [customColorInput, setCustomColorInput] = useState(
        getColorInputValue(currentColor)
    );

    useEffect(() => {
        toggle(isOpen);
        if (isOpen) {
            // Re-stage the selection to whatever color is currently active whenever the menu
            // (re)opens, so a previous unconfirmed pick doesn't leak into the next session.
            setSelectedColor(currentColor ?? fallbackColor);
            setCustomColorInput(getColorInputValue(currentColor));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, toggle]);

    const recentColors = getRecentCustomColors();

    const withCategoryColorIndex = (color: ColorIndex) =>
        new ColorIndex(
            color.mainColorIndex,
            currentColor?.categoryColorIndex ?? 0,
            color.customColor
        );

    const selectPreset = (index: number) => {
        setCustomColorInput(PRESET_COLORS[index]);
        setSelectedColor(new ColorIndex(index, currentColor?.categoryColorIndex ?? 0));
    };

    const selectCustom = (hex: string) => {
        setCustomColorInput(hex);
        setSelectedColor(withCategoryColorIndex(new ColorIndex(0, 0, hex)));
    };

    const hexPattern = /^#[0-9a-fA-F]{6}$/;

    // Native color <input> pickers require a genuinely trusted click to open their OS dialog,
    // which isn't reliable in every browser/embedding here. A typed hex code always works.
    const handleCustomTextChange = (value: string) => {
        setCustomColorInput(value);
        if (hexPattern.test(value)) {
            setSelectedColor(withCategoryColorIndex(new ColorIndex(0, 0, value)));
        }
    };

    return (
        <ControlledMenu
            {...menuProps}
            anchorPoint={anchorPoint}
            onClose={onClose}
            menuClassName="c-color-menu"
        >
            <MenuHeader className="c-color-menu__header">
                <span>{title}</span>
                <button
                    type="button"
                    className="c-color-menu__close"
                    title="Lukk fargevelger"
                    aria-label="Lukk fargevelger"
                    onClick={onClose}
                >
                    <i className="material-icons" aria-hidden="true">
                        close
                    </i>
                </button>
            </MenuHeader>
            <MenuDivider />
            <MenuHeader className="c-color-menu__label">Standardfarger</MenuHeader>
            <FocusableItem className="c-color-menu__palette">
                {() =>
                    PRESET_COLORS.map((hex, index) => {
                        const color = new ColorIndex(
                            index,
                            currentColor?.categoryColorIndex ?? 0
                        );
                        return (
                            <button
                                key={hex}
                                type="button"
                                className="c-color-menu__swatch"
                                style={{ backgroundColor: hex }}
                                aria-label={`Velg fargen ${hex}`}
                                aria-pressed={colorsEqual(selectedColor, color)}
                                onClick={() => selectPreset(index)}
                            />
                        );
                    })
                }
            </FocusableItem>
            {recentColors.length > 0 && (
                <>
                    <MenuDivider />
                    <MenuHeader className="c-color-menu__label">
                        Nylig brukte farger
                    </MenuHeader>
                    <FocusableItem className="c-color-menu__palette">
                        {() =>
                            recentColors.map(hex => (
                                <button
                                    key={hex}
                                    type="button"
                                    className="c-color-menu__swatch"
                                    style={{ backgroundColor: hex }}
                                    aria-label={`Velg nylig brukt farge ${hex}`}
                                    aria-pressed={colorsEqual(
                                        selectedColor,
                                        withCategoryColorIndex(new ColorIndex(0, 0, hex))
                                    )}
                                    onClick={() => selectCustom(hex)}
                                />
                            ))
                        }
                    </FocusableItem>
                </>
            )}
            <MenuDivider />
            <MenuHeader className="c-color-menu__label">Egendefinert farge</MenuHeader>
            <FocusableItem className="c-color-menu__custom">
                {({ ref }) => (
                    <>
                        <input
                            type="color"
                            className="c-color-menu__custom-input"
                            value={
                                hexPattern.test(customColorInput)
                                    ? customColorInput
                                    : '#000000'
                            }
                            onChange={e => selectCustom(e.target.value)}
                            aria-label="Velg egendefinert farge"
                        />
                        <input
                            ref={ref}
                            type="text"
                            className="c-color-menu__custom-text"
                            value={customColorInput}
                            onChange={e => handleCustomTextChange(e.target.value)}
                            placeholder="#rrggbb"
                            maxLength={7}
                            size={7}
                            spellCheck={false}
                            aria-label="Skriv inn egendefinert farge som heksadesimal kode"
                        />
                    </>
                )}
            </FocusableItem>
            <MenuDivider />
            <div className="c-color-menu__action-rows">
                {actionRows.map(row => (
                    <FocusableItem
                        key={row.title}
                        className={`c-color-menu__actions${
                            actionRows.length > 1 ? ' c-color-menu__actions--scoped' : ''
                        }`}
                    >
                        {({ ref }) => (
                            <>
                                {actionRows.length > 1 && (
                                    <span
                                        className="c-color-menu__scope"
                                        title={row.title}
                                        role="img"
                                        aria-label={row.title}
                                    >
                                        {row.icon}
                                    </span>
                                )}
                                {row.actions.map((action, actionIndex) => (
                                    <button
                                        key={action.title}
                                        ref={actionIndex === 0 ? ref : undefined}
                                        type="button"
                                        disabled={isActionDisabled(action, selectedColor)}
                                        className={`c-color-menu__action${
                                            action.className ? ` ${action.className}` : ''
                                        }`}
                                        title={getActionTitle(action, selectedColor)}
                                        aria-label={getActionTitle(action, selectedColor)}
                                        onClick={() => {
                                            action.onClick(selectedColor);
                                            onClose();
                                        }}
                                    >
                                        {action.icon}
                                    </button>
                                ))}
                            </>
                        )}
                    </FocusableItem>
                ))}
            </div>
        </ControlledMenu>
    );
};
